import {
  PlusIcon,
  TrashIcon,
  PencilSquareIcon,
} from '@heroicons/react/24/outline'
import Button from '@/components/misc/Button'
import { Button as ButtonCancelar } from '@/components/ui/button'
import { Dispatch, SetStateAction, useEffect, useState } from 'react'
import { useModalWizardStepsContext } from '@/components/new/modalWizardSteps/ModalWizardStepsContext'
import { InputForm } from '@/components/new/form/InputForm'
import ButtonLoading from '@/components/misc/Button'
import {
  ButtonPrimary,
  DataTable,
  Modal,
  SelectForm,
  TableContextProvider,
  TextAreaForm,
} from '@/components/new'
import { columns } from './columns'
import { ICost } from '@/containers/logistic/expedients/wizzardExpedient/tabs/cost/interfaces'
import { useForm } from 'react-hook-form'
import { Form } from '@/components/ui/form'
import { useModalWizardTabsContext } from '@/components/new/modalWizardTabs/ModalWizardTabsContext'
import { InputCurrencyForm } from '@/components/new/form/InputCurrencyForm'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { getCurrencies } from '@/api/currenciesAPI'
import { getCostCategories } from '@/api/costCategoriesAPI'
import { useAppSelector } from '@/store/hooks'
import {
  deleteContainerCosts,
  getContainerCosts,
  patchContainerCosts,
  postContainerCosts,
} from '@/api/containersAPI'
import { toast } from 'react-toastify'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import SpinnerLoading from '@/components/misc/SpinnerLoading'
import { IContainer } from '../../../interfaces/interfaces'
import useContainerWizzardSteps from '@/state/useContainerWizzardSteps'
import { SelectInterface } from '@/interfaces/InterfacesLocal'

const costTypes: SelectInterface[] = [
  {
    id: 'SUPPLIER',
    name: 'Proveedor',
  },
  {
    id: 'OPERATIONAL',
    name: 'Operacional',
  },
  {
    id: 'INDIRECT',
    name: 'Indirecto',
  },
]

export default function CostsContainerTab({
  isEditing,
  editContainer,
}: {
  isEditing?: boolean
  editContainer?: IContainer
}) {
  const context = isEditing
    ? useModalWizardTabsContext()
    : useModalWizardStepsContext()
  const { useFormWizzard } = context
  const [showCostModal, setShowCostModal] = useState(false)
  const [isSending, setIsSending] = useState(false)
  const [costTable, setCostTable] = useState<ICost[]>([])
  const [isCostEditing, setIsCostEditing] = useState(false)
  const [showDeleteCost, setShowDeleteCost] = useState(false)
  const [costToDelete, setCostToDelete] = useState<number>()
  const [selectedCategory, setSelectedCategory] = useState<number | null>(null)
  const data = useContainerWizzardSteps((state) => state.data)
  const updateData = useContainerWizzardSteps((state) => state.updateData)

  const { data: currencies } = useQuery({
    queryKey: ['currencies'],
    queryFn: getCurrencies,
  })
  const { data: costCategories } = useQuery({
    queryKey: ['costCategories'],
    queryFn: getCostCategories,
  })
  const queryClient = useQueryClient()

  const { data: containerCosts, isLoading } = useQuery({
    queryKey: ['container_costs', editContainer?.id],
    queryFn: () => getContainerCosts({ id: editContainer?.id as number }),
    enabled: Boolean(editContainer?.id),
    staleTime: 5 * 60 * 1000,
  })

  const postContainerCostsMutation = useMutation({
    mutationFn: postContainerCosts,
    onSuccess: () => {
      setIsSending(false)
      setShowCostModal(false)
      toast.success('El costo del contenedor se ha creado con éxito')
      queryClient.invalidateQueries({ queryKey: ['container_costs'] })

      form.reset()
      setSelectedCategory(null)
      setIsCostEditing(false)
    },
    onError: (response: any) => {
      setIsSending(false)
      toast.error(JSON.stringify(response.response.data.message))
    },
  })

  const updateContainerCostsMutation = useMutation({
    mutationFn: patchContainerCosts,
    onSuccess: () => {
      setIsSending(false)
      setShowCostModal(false)
      toast.success('El costo del contenedor se ha actualizado con éxito')
      queryClient.invalidateQueries({ queryKey: ['container_costs'] })

      form.reset()
      setSelectedCategory(null)
      setIsCostEditing(false)
    },
    onError: (response: any) => {
      setIsSending(false)
      toast.error(JSON.stringify(response.response.data.message))
    },
  })

  const deleteContainerCostsMutation = useMutation({
    mutationFn: deleteContainerCosts,
    onSuccess: () => {
      setIsSending(false)
      setShowDeleteCost(false)
      toast.success('El costo del contenedor se ha eliminado con éxito')
      queryClient.invalidateQueries({ queryKey: ['container_costs'] })

      form.reset()
      setSelectedCategory(null)
      setIsCostEditing(false)
    },
    onError: (response: any) => {
      setIsSending(false)
      toast.error(JSON.stringify(response.response.data.message))
    },
  })

  useEffect(() => {
    if (!isEditing) {
      const initialData = (Array.isArray(data.listCosts) ? data.listCosts : []).map((item: any, idx: number) => ({
        id: idx,
        cost: {
          amount: item.costAmount,
          codeCurrency: item.costCodecurrency?.toUpperCase?.() ?? '',
        },
        category: item.category || '',
        observations: item.observations || '',
        exchangeRate: item.costExchangeRate,
        actions: {
          remove: true,
          edit: true,
        },
      }))
      setCostTable((prev) => [...prev, ...(initialData as ICost[])])
    }
  }, [])

  useEffect(() => {
    if (isEditing) {
      if (!isLoading && containerCosts && Array.isArray(containerCosts.fixedCosts)) {
        const initialData = containerCosts.fixedCosts.map((item) => ({
          id: item.id,
          cost: {
            amount: item?.amountPrice ?? '-',
            codeCurrency: item?.codeCurrency?.toUpperCase() ?? '-',
          },
          category: costCategories?.find(
            (elem) => elem?.id === item?.fixedCostCategory?.id
          )?.name,
          observations: item?.observations ?? '-',
          actions: {
            remove: true,
            edit: true,
          },
        }))

        setCostTable(initialData as ICost[])
      } else if (!isLoading && (!containerCosts || !Array.isArray(containerCosts.fixedCosts))) {
        setCostTable([])
      }
    }
  }, [containerCosts, isLoading])

  const { business } = useAppSelector((state) => state.init)

  const form = useForm()
  const saveCost = (cost: any) => {
    const fixedCostCategoryId = cost.category
    const price = {
      amount: cost.cost.amount,
      codeCurrency: cost.cost.codeCurrency,
    }
    const exchangeRate = Number(cost.exchangeRate)
    const id = cost.id
    const observations = cost.observations

    if (isEditing) {
      if (isCostEditing) {
        setIsSending(true)
        updateContainerCostsMutation.mutate({
          id,
          params: {
            fixedCostCategoryId,
            price,
            observations,
          },
        })
      } else {
        setIsSending(true)
        postContainerCostsMutation.mutate({
          id: editContainer?.id as number,
          params: {
            fixedCostCategoryId,
            price,
            observations,
          },
        })
      }
    } else {
      if (isCostEditing) {
        // Editar costo existente
        updateData({
            ...data,
          listCosts: (Array.isArray(data.listCosts) ? data.listCosts : []).map((item, idx) =>
            idx === id
              ? {
                  costAmount: cost.cost.amount,
                  costExchangeRate: cost.exchangeRate,
                  costCodecurrency: cost.cost.codeCurrency,
                  category: costCategories?.find((cat) => cat.id === cost.category)?.name || '',
                  observations: cost.observations || '',
                  type: cost.type || 'UNDEFINED',
                }
              : item
          ),
          })
        setCostTable((prev) =>
          prev.map((item) =>
            item.id === id
              ? {
                  ...item,
                  cost: {
                    amount: cost.cost.amount,
                    codeCurrency: cost.cost.codeCurrency.toUpperCase(),
                  },
                  category: costCategories?.find((cat) => cat.id === cost.category)?.name || '',
                  observations: cost.observations || '',
                  exchangeRate: cost.exchangeRate,
                }
              : item
          )
        )
        toast.success('El costo del contenedor se ha editado con éxito')
      } else {
        // Añadir nuevo costo
        updateData({
          ...data,
          listCosts: [
            ...((Array.isArray(data.listCosts) ? data.listCosts : [])),
            {
              costAmount: cost.cost.amount,
              costExchangeRate: cost.exchangeRate,
              costCodecurrency: cost.cost.codeCurrency,
              category: costCategories?.find((cat) => cat.id === cost.category)?.name || '',
              observations: cost.observations || '',
              type: cost.type || 'UNDEFINED',
            },
          ],
        })
        setCostTable((prev) => [
            ...prev,
            {
              id: prev.length,
              cost: {
                amount: cost.cost.amount,
                codeCurrency: cost.cost.codeCurrency.toUpperCase(),
              },
            category: costCategories?.find((cat) => cat.id === cost.category)?.name || '',
            observations: cost.observations || '',
            exchangeRate: cost.exchangeRate,
              actions: {
                remove: true,
                edit: true,
              },
            },
          ])
        toast.success('El costo del contenedor se ha añadido con éxito')
      }
      form.reset()
      setShowCostModal(false)
      setSelectedCategory(null)
      setIsCostEditing(false)
    }
  }

  return (
    <div className={'h-[60dvh]'}>
      {isLoading ? (
        <SpinnerLoading />
      ) : (
        <TableContextProvider
          data={costTable.filter(item => item && typeof item.cost?.amount === 'number' && item.cost?.codeCurrency)}
          isLoading={false}
          leftButtons={[
            {
              type: 'text',
              label: 'Listado de costos',
            },
          ]}
          rightButtons={[
            {
              type: 'button',
              icon: <PlusIcon strokeWidth={2} className="h-5 w-5 text-black" />,
              label: 'Añadir costo',
              onClick: () => setShowCostModal(true),
            },
          ]}
        >
          <DataTable
            columns={[
              ...columns,
              {
                accessorKey: 'actions',
                header: 'Acciones',
                cell: ({ row }) => {
                  return (
                    <div className={'flex justify-center'}>
                      {row.original.actions.edit && (
                        <div
                          role={'button'}
                          className={`p-2`}
                          onClick={() => {
                            form.setValue('id', row.original.id)
                            form.setValue('cost', {
                              amount: row.original.cost?.amount,
                              codeCurrency: row.original.cost?.codeCurrency,
                            })
                            form.setValue('exchangeRate', row.original.exchangeRate)
                            setShowCostModal(true)
                            setIsCostEditing(true)
                          }}
                        >
                          <PencilSquareIcon
                            className={`h-5 w-5 text-blue-700`}
                          />
                        </div>
                      )}
                      <div
                        role={'button'}
                        className={`p-2`}
                        onClick={() => {
                          setShowDeleteCost(true)
                          setCostToDelete(row.original.id)
                        }}
                      >
                        <TrashIcon className={`h-5 w-5 text-red-700`} />
                      </div>
                    </div>
                  )
                },
              },
            ]}
            storageKey={'DocumentsExpTab'}
          />
        </TableContextProvider>
      )}
      {showCostModal && (
        <Modal
          isOpen={showCostModal}
          onClose={() => {
            setIsCostEditing(false)
            setShowCostModal(false)
            setSelectedCategory(null)
            form.reset()
          }}
          title={isCostEditing ? 'Editar costo' : 'Añadir costo'}
          size={'xs'}
          showDialogAlert={false}
        >
          <div className={'flex w-full flex-col'}>
            <Form {...form}>
              <div className={'flex w-full flex-col space-y-2'}>
                {currencies && (
                  <InputCurrencyForm
                    currenciesData={currencies?.map(({ code }) => ({
                      code: code,
                      displayName: code,
                    }))}
                    name={'cost'}
                    control={form.control}
                    label={'Costo'}
                    rules={{
                      required: 'Campo requerido',
                    }}
                  />
                )}
                {form.watch('cost')?.codeCurrency !== business?.costCurrency &&
                  form.watch('cost')?.codeCurrency !== undefined && (
                    <InputForm
                      name={'exchangeRate'}
                      label={'Tasa de cambio'}
                      control={form.control}
                      placeholder={'Tasa de cambio'}
                      type="number"
                      rules={{
                        required: 'Campo requerido',
                      }}
                    />
                  )}
                <SelectForm
                  name={'category'}
                  data={
                    costCategories?.map(({ id, name }) => ({
                      id,
                      title: name,
                    })) ?? []
                  }
                  control={form.control}
                  label={'Categoría de costo'}
                  rules={{
                    required: 'Campo requerido',
                  }}
                  defaultValue={selectedCategory}
                />
                <SelectForm
                  name={'type'}
                  data={[
                    { id: 'SUPPLIER', title: 'Proveedor' },
                    { id: 'OPERATIONAL', title: 'Operacional' },
                    { id: 'INDIRECT', title: 'Indirecto' },
                    { id: 'UNDEFINED', title: 'Indefinido' },
                  ]}
                  control={form.control}
                  label={'Tipo de costo'}
                  rules={{ required: 'Campo requerido' }}
                />
                <TextAreaForm
                  name={'observations'}
                  control={form.control}
                  label={'Observaciones'}
                />
              </div>
              <div className={'mt-10 flex w-full justify-between'}>
                <ButtonCancelar
                  disabled={isSending}
                  variant="outline"
                  onClick={() => {
                    setIsCostEditing(false)
                    setShowCostModal(false)
                    setSelectedCategory(null)
                    form.reset()
                  }}
                >
                  Cancelar
                </ButtonCancelar>
                {isSending ? (
                  <ButtonLoading
                    loading={isSending}
                    name="Guardando..."
                  ></ButtonLoading>
                ) : (
                  <ButtonPrimary
                    onClick={form.handleSubmit(saveCost)}
                    text={'Guardar'}
                  />
                )}
              </div>
            </Form>
          </div>
        </Modal>
      )}
      <AlertDialog open={showDeleteCost}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar el costo?</AlertDialogTitle>
            <AlertDialogDescription>
              Si elimina ahora, perderá todos los datos asociados a este costo.
              ¿Está seguro de que desea eliminar?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setShowDeleteCost(false)}>
              Cancelar
            </AlertDialogCancel>
            <Button
              name={'Sí, eliminar'}
              loading={isSending}
              disabled={isSending}
              action={() => {
                                 if (isEditing) {
                   setIsSending(true)
                   deleteContainerCostsMutation.mutate(costToDelete as number)
                } else {
                  setCostTable((prev) => prev.filter((item) => item.id !== costToDelete))
                  updateData({
                    ...data,
                    listCosts: data.listCosts.filter((_, idx) => idx !== costToDelete),
                  })
                  setShowDeleteCost(false)
                  toast.success('El costo del contenedor se ha eliminado con éxito')
                }
              }}
            />
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
